<template>
  <IconDefs />
  <!-- Arena Background -->
  <div class="arena-background"></div>

  <!-- Skip Navigation Links -->
  <nav aria-label="Skip navigation" class="relative z-[10000]">
    <a href="#main-content" class="skip-link">{{ STRINGS.ui.navigation.skipToMain }}</a>
  </nav>

  <Toast
    position="top-center"
    :pt="{
      root: { class: 'p-toast' },
      message: (opts: any) => ({
        class: ['p-toast-message', `p-toast-message-${opts.state?.message?.severity || 'info'}`],
      }),
      messageContent: { class: 'p-toast-message-content' },
      summary: { class: 'p-toast-summary' },
    }"
  />
  <CookieConsentBanner />
  <!-- Document scroll lock lives HERE (the game shell), not on the shared body,
       so it never leaks to the admin app that imports the same stylesheet. -->
  <div id="app" class="min-h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col relative z-[1]">
    <!-- Auth loading state -->
    <div
      v-if="!auth.authCheckComplete.value"
      class="flex-1 flex flex-col items-center justify-center min-h-[100dvh]"
      role="status"
      :aria-busy="true"
      :aria-label="STRINGS.aria.labels.authLoading"
    >
      <h1
        class="font-display text-arena-text-bright mb-6 font-extrabold tracking-[0.08em]"
        style="font-size: clamp(48px, 10vw, 72px)"
      >
        {{ STRINGS.branding.title }}
      </h1>
      <LoadingSpinner />
    </div>

    <!-- Email Verification Page -->
    <main
      v-else-if="verificationToken"
      id="main-content"
      role="main"
      aria-label="Email verification"
      tabindex="-1"
    >
      <EmailVerification :token="verificationToken" @continue="clearVerificationToken" />
    </main>

    <!-- Auth Gate: show login/signup when unauthenticated and no game in progress -->
    <main
      v-else-if="!auth.isAuthenticated.value && gameState === 'matchmaking'"
      id="main-content"
      role="main"
      :aria-label="STRINGS.aria.labels.authentication"
      tabindex="-1"
    >
      <AuthScreen :invite-token="inviteToken" />
    </main>

    <template v-else>
      <!-- Settings gear FAB — fixed top-right, hidden during timed phases -->
      <button
        v-if="gameState !== 'selecting-letters' && gameState !== 'in-progress'"
        class="settings-fab"
        @click="showSettingsHub = true"
        :aria-label="STRINGS.aria.labels.openSettings"
        data-testid="settings-fab"
      >
        <AppIcon name="gear" :size="18" />
      </button>

      <!-- Global overlays -->
      <SettingsHub :visible="showSettingsHub" @close="showSettingsHub = false" />
      <CodexPanel
        :visible="tutorial.codexOpen.value"
        :show-welcome="!tutorial.hasSeen.value"
        :has-new-rule="!tutorial.hasAcknowledgedRules.value"
        @close="tutorial.closeCodex()"
      />
      <BriefingRoomOverlay
        :visible="tutorial.briefingOpen.value"
        @complete="onBriefingComplete()"
      />
      <RuleChangeModal
        :visible="tutorial.ruleChangeOpen.value"
        @acknowledged="onRuleChangeAcknowledged()"
        @see-rules="
          tutorial.openCodex();
          onRuleChangeAcknowledged();
        "
      />
      <FeedbackModal :visible="showFeedbackModal" @close="showFeedbackModal = false" />

      <Transition name="game-state" mode="out-in">
        <!-- Matchmaking + Match Found Phase -->
        <!-- One continuous Direction A screen: GameMatchmaking stays mounted
             through match-found and morphs its orb into the "found" state, so
             there is no hard cut to a separate face-off view. -->
        <main
          v-if="gameState === 'matchmaking' || gameState === 'match-found'"
          key="matchmaking"
          id="main-content"
          role="main"
          :aria-label="
            gameState === 'match-found'
              ? STRINGS.aria.labels.matchFound
              : STRINGS.aria.labels.gameMatchmaking
          "
          tabindex="-1"
        >
          <GameMatchmaking
            :auto-start="autoStartMatchmaking"
            :match-found-info="gameState === 'match-found' ? matchFoundInfo : null"
          />
        </main>

        <!-- Letter Selection Phase -->
        <main
          v-else-if="gameState === 'selecting-letters'"
          key="selecting-letters"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.letterSelection"
          tabindex="-1"
        >
          <LetterSelection
            :game-id="gameId"
            :players="players"
            :current-player-id="currentPlayerId"
            :selected-letters="selectedLetters"
            :selector-player-id="selectorPlayerId"
            :selection-enabled="selectionEnabled"
            :vowel-button-enabled="vowelButtonEnabled"
            :consonant-button-enabled="consonantButtonEnabled"
            :selection-status="selectionStatus"
            :timer-time-remaining="timerState.timeRemaining.value"
            :timer-total-time="selectionTimerDuration"
            :timer-is-active="timerState.isActive.value && gameState === 'selecting-letters'"
            :ws-connection-status="connectionStatus"
            :manual-reconnect-available="manualReconnectAvailable"
            @error="handleError"
          />
        </main>

        <!-- Game In Progress Phase - Timer Word Finding -->
        <main
          v-else-if="gameState === 'in-progress'"
          key="in-progress"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.wordFinding"
          tabindex="-1"
        >
          <WordFindingPhase
            :time-remaining="timerState.timeRemaining.value"
            :is-active="timerState.isActive.value"
            :is-paused="timerState.isPaused?.value ?? false"
            :server-synced="timerState.serverSynced.value"
            :is-urgent="timerState.isUrgent.value"
            :display-seconds="timerState.displaySeconds.value"
            :selected-letters="selectedLetters"
            :word-submitted="wordSubmitted"
            :opponent-submitted="opponentSubmitted"
            :current-word="currentWord"
            :submission-type="submissionType"
            :can-submit-word="canSubmitWord"
            :submit-button-text="submitButtonText"
            :current-player-status="currentPlayerStatus"
            :opponent-status="opponentStatus"
            :opponent-name="opponentName"
            :is-connection-error-visible="isConnectionErrorVisible"
            :connection-error-type="connectionErrorType"
            :reconnection-state="reconnectionState"
            @update:current-word="currentWord = $event"
            @submit-word="handleWordSubmit"
            @retry-connection="handleRetryConnection"
          />
        </main>

        <!-- Awaiting New Round Phase -->
        <main
          v-else-if="gameState === 'awaiting-new-round'"
          key="awaiting-new-round"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.waitingForNextRound"
          tabindex="-1"
        >
          <div
            class="flex flex-col items-center justify-center gap-4 p-8 min-h-[100dvh] max-h-[100dvh] overflow-y-auto text-center"
          >
            <div class="rematch-spinner" aria-hidden="true"></div>
            <h2 class="text-2xl font-bold text-arena-text-bright m-0">
              Waiting for {{ opponentName }}...
            </h2>
            <p class="text-base text-arena-text-muted m-0">
              {{ STRINGS.status.game.nextRoundSent }}
            </p>
            <Button
              class="py-4 px-6 bg-transparent text-arena-text-bright border-2 border-arena-border rounded-xl text-base font-semibold cursor-pointer min-w-[120px] min-h-[44px] transition-all duration-150 hover:bg-energy-primary/10 hover:border-energy-primary/30 hover:-translate-y-px active:translate-y-0"
              :label="STRINGS.ui.buttons.cancel"
              :unstyled="true"
              @click="onReturnHome"
              data-testid="cancel-new-round-button"
            >
              {{ STRINGS.ui.buttons.cancel }}
            </Button>
          </div>
        </main>

        <!-- Game Results Phase -->
        <main
          v-else-if="gameState === 'round-finished'"
          key="round-finished"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.roundResults"
          tabindex="-1"
        >
          <RoundResults
            :round-results="roundResults!"
            :selected-letters="selectedLetters"
            :current-player-id="currentPlayerId"
            :new-round-pending="newRoundPending"
            :opponent-wants-new-round="opponentWantsNewRound"
            :cumulative-scores="cumulativeScores"
            :round-number="roundNumber"
            :best-word="bestWord"
            :queue-available="queueAvailable"
            :show-race-condition-dialog="showRaceConditionDialog"
            @play-again="handlePlayAgain"
            @return-home="onReturnHome"
            @error="handleError"
            @try-match-waiting-player="handleTryMatchWaitingPlayer"
            @play-solo="handlePlaySolo"
            @find-new-match-from-dialog="onFindNewMatchFromDialog"
          />
        </main>

        <!-- Game Over Summary -->
        <main
          v-else-if="gameState === 'game-over'"
          key="game-over"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.matchComplete"
          tabindex="-1"
        >
          <GameOverSummary
            :players="players"
            :current-player-id="currentPlayerId"
            :cumulative-scores="cumulativeScores"
            :round-number="roundNumber"
            :round-results="roundResults"
            :best-word="bestWord"
            :selected-letters="selectedLetters"
            @find-new-match="onFindNewMatch"
            @return-home="onReturnHome"
            @error="handleError"
          />
        </main>

        <!-- Error State -->
        <main
          v-else-if="gameState === 'error'"
          key="error"
          id="main-content"
          role="main"
          :aria-label="STRINGS.aria.labels.errorState"
          tabindex="-1"
          class="grow flex flex-col justify-center items-center p-8 text-center max-[479px]:p-4"
        >
          <div class="flex flex-col items-center gap-6 error-content-animated">
            <h1
              class="text-energy-danger m-0 error-title-glitch text-xl min-[480px]:text-2xl md:text-3xl"
            >
              {{ STRINGS.errors.connection.title }}
            </h1>
            <p
              class="text-arena-text-bright m-0 max-w-[500px] text-sm min-[480px]:text-base md:text-lg"
            >
              {{ errorMessage }}
            </p>
            <button
              @click="onReconnect"
              class="error-retry-pulse inline-flex items-center justify-center py-4 px-8 rounded-xl font-semibold text-lg min-h-[44px] min-w-[44px] transition-all duration-300 cursor-pointer w-full max-w-[280px] whitespace-nowrap bg-primary-500 text-white border-none hover:bg-primary-600 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(201,168,76,0.3)] active:bg-primary-700 active:translate-y-0 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2 focus-visible:shadow-[0_0_0_4px_rgba(201,168,76,0.6)]"
            >
              {{ STRINGS.ui.buttons.tryAgain }}
            </button>
          </div>
        </main>
      </Transition>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import IconDefs from './components/ui/icons/IconDefs.vue';
import LoadingSpinner from './components/ui/LoadingSpinner.vue';
import EmailVerification from './components/EmailVerification.vue';
import AuthScreen from './components/AuthScreen.vue';
import { useAuth } from '@/composables/useAuth';
import ComponentError from './components/ui/ComponentError.vue';
import WordFindingPhase from './components/WordFindingPhase.vue';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import { useWebSocket } from '@/composables/useWebSocket';
import { useServerAuthoritativeTimer } from '@/composables/useServerAuthoritativeTimer';
import { useGameFlow } from '@/composables/useGameFlow';
import { useGameSession } from '@/composables/useGameSession';
import { usePlayerName } from '@/composables/usePlayerName';
import { useConnectionErrorHandling } from '@/composables/useConnectionErrorHandling';
import { useGameSnapshot } from '@/composables/useGameSnapshot';
import { useTutorialState } from '@/composables/useTutorialState';
import { WS_DISCONNECT_REASONS } from '@/types/websocket';
import STRINGS from '@/constants/strings';
import CodexPanel from './components/CodexPanel.vue';
import BriefingRoomOverlay from './components/BriefingRoomOverlay.vue';
import RuleChangeModal from './components/RuleChangeModal.vue';
import FeedbackModal from './components/FeedbackModal.vue';
import SettingsHub from './components/SettingsHub.vue';
import AppIcon from './components/ui/icons/AppIcon.vue';
import CookieConsentBanner from './components/CookieConsentBanner.vue';
import { useVersionCheck } from '@/composables/useVersionCheck';

const auth = useAuth();
useVersionCheck();

// Email verification: detect ?token= in URL (skip on /signup path)
const verificationToken = ref<string | null>(
  (() => {
    if (window.location.pathname === '/signup') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  })()
);

// Invite token: detect ?token= on /signup path
const inviteToken = ref<string | null>(
  (() => {
    if (window.location.pathname !== '/signup') return null;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      window.history.replaceState({}, '', '/signup');
    }
    return token;
  })()
);

const clearVerificationToken = () => {
  verificationToken.value = null;
  // Clean up URL without reload
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, '', url.toString());
};

// Lazy load heavy components that are only used in specific game states
const GameMatchmaking = defineAsyncComponent({
  loader: () => import('./components/GameMatchmaking.vue'),
  loadingComponent: LoadingSpinner,
  errorComponent: ComponentError,
  delay: 200,
  timeout: 10000,
});

const LetterSelection = defineAsyncComponent({
  loader: () => import('./components/LetterSelection.vue'),
  loadingComponent: LoadingSpinner,
  errorComponent: ComponentError,
  delay: 200,
  timeout: 10000,
});

const RoundResults = defineAsyncComponent({
  loader: () => import('./components/RoundResults.vue'),
  loadingComponent: LoadingSpinner,
  errorComponent: ComponentError,
  delay: 200,
  timeout: 10000,
});

const GameOverSummary = defineAsyncComponent({
  loader: () => import('./components/GameOverSummary.vue'),
  loadingComponent: LoadingSpinner,
  errorComponent: ComponentError,
  delay: 200,
  timeout: 10000,
});

const {
  connectionStatus,
  lastError,
  reconnectAttempt,
  maxReconnectAttempts,
  sendMessage,
  onMessage,
  manualReconnect,
  manualReconnectAvailable,
  connect,
} = useWebSocket();

const timerState = useServerAuthoritativeTimer();

// --- Session persistence for reconnection ---
const gameSession = useGameSession();
const playerNameStore = usePlayerName();

// Lazy callback wrapper to break the circular dependency between useGameFlow and useGameSnapshot:
// useGameFlow needs onResetTimerPhase at construction time;
// useGameSnapshot needs gameId/gameState/etc. from useGameFlow.
let _resetTimerPhaseState: () => void = () => {};
const onResetTimerPhase = () => _resetTimerPhaseState();

const {
  gameState,
  gameId,
  players,
  currentPlayerId,
  selectedLetters,
  roundResults,
  errorMessage,
  newRoundPending,
  opponentWantsNewRound,
  cumulativeScores,
  roundNumber,
  wordSubmitted,
  opponentSubmitted,
  selectorPlayerId,
  selectionEnabled,
  vowelButtonEnabled,
  consonantButtonEnabled,
  selectionStatus,
  autoStartMatchmaking,
  matchFoundInfo,
  bestWord,
  queueAvailable,
  showRaceConditionDialog,
  recordMatchFound,
  handleSnapshot,
  handlePlayAgain,
  handleReturnHome,
  handleFindNewMatch,
  handleError,
  handleReconnect,
  handleQueueStatus,
  handleTryMatchWaitingPlayer,
  handleWaitingPlayerNotFound,
  handlePlaySolo,
  handleFindNewMatchFromDialog,
} = useGameFlow({
  stopTimer: () => timerState.stopTimer(),
  onResetTimerPhase,
  sendMessage,
});

const {
  currentWord,
  selectionTimerDuration,
  submissionType,
  canSubmitWord,
  submitButtonText,
  currentPlayerStatus,
  opponentStatus,
  handleWordSubmit,
  resetTimerPhaseState,
} = useGameSnapshot({
  timerState,
  gameId,
  gameState,
  wordSubmitted,
  opponentSubmitted,
  handleSnapshot,
  handleError,
  gameSession,
  playerNameStore,
  sendMessage,
  onMessage,
});

// Wire the lazy callback now that useGameSnapshot has been constructed
_resetTimerPhaseState = resetTimerPhaseState;

const opponentName = computed(
  () =>
    players.value.find(p => p.id !== currentPlayerId.value)?.name ??
    STRINGS.status.player.opponentFallback
);

// Tutorial state
const tutorial = useTutorialState();

const route = useRoute();
const router = useRouter();

const showFeedbackModal = ref(false);
const showSettingsHub = ref(false);

// When visiting /contact, open the feedback modal after auth resolves.
// Requires verified email to prevent spam from throwaway accounts.
watch(
  [
    auth.authCheckComplete,
    auth.isAuthenticated,
    auth.isEmailVerified,
    () => route.meta.openContact,
  ],
  ([checkComplete, isAuthenticated, isEmailVerified, openContact]) => {
    if (checkComplete && isAuthenticated && isEmailVerified && openContact) {
      showFeedbackModal.value = true;
      router.replace('/');
    }
  },
  { immediate: true }
);

function onBriefingComplete() {
  tutorial.closeBriefing();
  tutorial.markSeen();
  tutorial.markRulesAcknowledged();
}

function onRuleChangeAcknowledged() {
  tutorial.closeRuleChange();
  tutorial.markRulesAcknowledged();
}

const { connectionErrorType, reconnectionState, isConnectionErrorVisible, handleRetryConnection } =
  useConnectionErrorHandling({
    gameState,
    connectionStatus,
    lastError,
    reconnectAttempt,
    maxReconnectAttempts,
    manualReconnect,
    handleError,
  });

// Wrap handlers that should clear the session
const onReturnHome = () => {
  gameSession.clear();
  handleReturnHome();
};

const onFindNewMatch = () => {
  gameSession.clear();
  handleFindNewMatch();
};

const onFindNewMatchFromDialog = () => {
  gameSession.clear();
  handleFindNewMatchFromDialog();
};

const onReconnect = () => {
  gameSession.clear();
  handleReconnect();
};

// Watch for connection status changes to trigger reconnection
watch(connectionStatus, status => {
  if (status === 'connected') {
    const session = gameSession.load();
    if (session) {
      sendMessage({
        ReconnectRequest: {
          game_id: session.game_id,
          player_id: session.player_id,
          last_known_server_time: null,
        },
      });
    }
  }
});

// Auth session check on mount, then auto-connect if needed.
// Order matters: checkSession must complete before WS connect so the
// auth loading gate (authCheckComplete) can render the correct initial UI.
// Without this, child components that derive player identity from authUser
// would render with stale/empty auth state.
onMounted(async () => {
  await auth.checkSession();

  const savedSession = gameSession.load();
  if (savedSession) {
    try {
      await connect();
    } catch (err) {
      reportClientError(err, ErrorLocation.AppAutoConnectFailed);
      gameSession.clear();
    }
  }
});

/**
 * WebSocket message handlers
 */
const unsubscribeFunctions: (() => void)[] = [];

// Match-found transition handler
unsubscribeFunctions.push(
  onMessage('OpponentFound', data => {
    recordMatchFound({
      opponentName: data.opponent_name,
      isBotGame: data.is_bot_game,
      countdownDurationMs: data.countdown_duration_ms,
    });
  })
);

// Reconnection handlers
unsubscribeFunctions.push(
  onMessage('ReconnectionSuccess', _data => {
    // Reconnection acknowledged; game state will be restored by StateRestored snapshot
  })
);

unsubscribeFunctions.push(
  onMessage('ReconnectionFailed', _data => {
    gameSession.clear();
    handleReconnect();
  })
);

// Queue availability updates
unsubscribeFunctions.push(
  onMessage('QueueStatus', data => {
    handleQueueStatus(data.available);
  })
);

// Waiting player not found (race condition: player left queue before match attempt)
unsubscribeFunctions.push(
  onMessage('WaitingPlayerNotFound', () => {
    handleWaitingPlayerNotFound();
  })
);

// Matchmaking acknowledgments — handlers must exist at WS scope so messages
// are never silently dropped. player_id arrives again via GameSnapshot.GameStarted.
unsubscribeFunctions.push(onMessage('FindingMatch', _data => {}));
unsubscribeFunctions.push(onMessage('WaitingForOpponent', _data => {}));

// Session-expiry errors trigger logout; all other server errors surface via handleError.
unsubscribeFunctions.push(
  onMessage('Error', data => {
    if (
      data.message.includes(WS_DISCONNECT_REASONS.SESSION_EXPIRED) ||
      data.message.includes(WS_DISCONNECT_REASONS.AUTHENTICATION_REQUIRED)
    ) {
      gameSession.clear();
      auth.logout().catch((err: unknown) => {
        console.warn('Logout failed during session expiry handling:', err);
      });
    } else {
      handleError(data.message);
    }
  })
);

onUnmounted(() => {
  unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
});
</script>

<style scoped>
/* =============================================
   SETTINGS FAB
   ============================================= */
.settings-fab {
  position: fixed;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 8800;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--color-arena-border, #3d3428);
  background: var(--color-arena-surface, #1a1612);
  color: var(--color-arena-text-muted, #8a7e6a);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 150ms,
    border-color 150ms,
    box-shadow 150ms,
    transform 150ms;
}

.settings-fab:hover {
  color: var(--color-energy-primary, #c9a84c);
  border-color: rgba(201, 168, 76, 0.4);
  box-shadow: 0 0 12px rgba(201, 168, 76, 0.2);
  transform: rotate(30deg);
}
</style>
